import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import ThumbUpAltRoundedIcon from '@mui/icons-material/ThumbUpAltRounded';

const items = [
  {
    icon: <SettingsSuggestRoundedIcon sx={{ color: '#007867', fontSize: 40 }}/>,
    title: 'Adaptable performance',
    description:
      'Our product offers businesses valuable insights into their customers financial behaviors, helping them make informed decisions and tailor their products or services accordingly.',
  },
  {
    icon: <ConstructionRoundedIcon sx={{ color: '#007867', fontSize: 40 }} />,
    title: 'Built for accuracy',
    description:
      'Experience unmatched accuracy that goes above and beyond with lasting investment. Lume delivers precise risk assessments and intelligent loan affordability insights.',
  },
  {
    icon: <ThumbUpAltRoundedIcon sx={{ color: '#007867', fontSize: 40 }}/>,
    title: 'Great user experience',
    description:
      'Integrate our product into your routine with an intuitive and easy-to-use interface.',
  },
  {
    icon: <AutoFixHighRoundedIcon sx={{ color: '#007867', fontSize: 40 }} />,
    title: 'Financial Resilience Assessment',
    description:
      "Lume assesses a customer's ability to manage debt and maintain repayments during periods of income disruption, helping lenders make informed, risk-aware credit decisions.",
  },
  {
    icon: <BarChartRoundedIcon sx={{ color: '#007867', fontSize: 40 }}/>,
    title: 'Income & Expenditure Insights',
    description:
      'Lume analyzes transaction data to assess income reliability and track monthly spending patterns. This helps lenders understand affordability and financial behavior before making lending decisions.',
  },
  {
    icon: <QueryStatsRoundedIcon sx={{ color: '#007867', fontSize: 40 }}/>,
    title: 'Behaviour Analysis',
    description:
      'Lume sheds light to your customers spending habits, their savings and expense patterns, categorizing these habits to provide insights into their financial behavior.',
  },
];

export default function Highlights() {
  return (
    <Box
      id="highlights"
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 8, sm: 16 },
        color: '#161C24',
        background: 'linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #007867 0%, #5BE49B 50%, #007867 100%)',
        }
      }}
    >
      <Container
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 3, sm: 6 },
        }}
      >
        <Box
          sx={{
            width: { sm: '100%', md: '60%' },
            textAlign: { sm: 'left', md: 'center' },
          }}
        >
          <Typography
            component="h2"
            variant="h3"
            fontWeight={700}
            sx={{
              background: 'linear-gradient(135deg, #007867 0%, #5BE49B 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2
            }}
          >
            Highlights
          </Typography>
          <Typography mt={2} variant="body1" sx={{ color: '#4c4c4c', fontSize: '1.1rem', lineHeight: 1.8 }}>
            Explore why Lume stands out: adaptability, accuracy,
            user-friendly design, and Behaviour analysis. Enjoy reliable accuracy and
            precision in Credit Scoring.
          </Typography>
        </Box>
        <Grid container spacing={2.5}>
          {items.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Stack
                direction="column"
                color="inherit"
                component={Card}
                spacing={1}
                useFlexGap
                sx={{
                  p: 3,
                  height: '100%',
                  border: '2px solid',
                  borderColor: 'transparent',
                  background: 'white',
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0, 120, 103, 0.08)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #007867 0%, #5BE49B 100%)',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(0, 120, 103, 0.15)',
                    borderColor: '#5BE49B',
                    '&::before': {
                      opacity: 1,
                    },
                    '& .highlight-icon': {
                      transform: 'scale(1.1) rotate(5deg)',
                      color: '#5BE49B',
                    }
                  }
                }}
              >
                <Box
                  className="highlight-icon"
                  sx={{
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 60,
                    height: 60,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(0, 120, 103, 0.1) 0%, rgba(91, 228, 155, 0.1) 100%)',
                    mb: 1
                  }}
                >
                  {item.icon}
                </Box>
                <div>
                  <Typography
                    fontWeight={700}
                    gutterBottom
                    sx={{
                      color: '#2c3e50',
                      fontSize: '1.15rem',
                      mb: 1.5
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#4c4c4c',
                      lineHeight: 1.7,
                      fontSize: '0.95rem'
                    }}
                  >
                    {item.description}
                  </Typography>
                </div>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}